import { useEffect, useMemo, useState } from "react";
import { Card } from "@mui/material";
import SoftBox from "components/SoftBox";
import PageLayout from "examples/LayoutContainers/PageLayout";
import Loader from "examples/Loader";
import PageTitle from "../../components/PageTitle";
import { useAgencyInfo } from "context/agency";
import { useAppServices } from "hook/services";
import OverviewHeader from "./components/OverviewHeader";
import StatOverviewCard from "./components/StatOverviewCard";
import GrowthCard from "./components/GrowthCard";
import MarketplaceHealthCard from "./components/MarketplaceHealthCard";
import MonetizationCard from "./components/MonetizationCard";
import DirectoryHealthCard from "./components/DirectoryHealthCard";
import FunnelSnapshotCard from "./components/FunnelSnapshotCard";
import NextBestActionsCard from "./components/NextBestActionsCard";
import "./dashboardOverview.css";
import "./dashboardPageCompact.css";
import "../../components/shared/primaryButton.css";
import { useKnowledgeBase } from "context/knowledgebaseContext";

function Default() {
  const [agency, Update] = useAgencyInfo();
  const AppService = useAppServices();
  const activities = useAgencyInfo()[5];
  const { knowledgeBase } = useKnowledgeBase();
  console.log(activities, "activities");

  const [selectedRange, setSelectedRange] = useState("7D");
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState({ code: "USD", symbol: "$" });

  useEffect(() => {
    if (agency?.currency) {
      setCurrency(agency.currency);
    }
  }, [agency?.currency]);

  const [growthSeries, setGrowthSeries] = useState({
    claimed: [],
    listings: [],
    premium: [],
  });

  const [stats, setStats] = useState({
    allListings: 0,
    freeListings: 0,
    premiumListings: 0,
    claimedListings: 0,
    reviews: 0,
    blogs: 0,
    events: 0,
    news: 0,
    jobs: 0,
    visitors: 0,
    consumerAccountsWithPlans: 0,
  });

  useEffect(() => {
    const loadDashboardStats = async () => {
      if (!agency?._id) return;

      setLoading(true);

      try {
        const res = await AppService.accounts.statistics({
          query: `agency_id=${agency._id}&range=${selectedRange}`,
        });

        const data = res?.response?.data || {};

        setStats({
          allListings: data.allListings || 0,
          freeListings: data.freeListings || 0,
          premiumListings: data.premiumListings || 0,
          claimedListings: data.claimedListings || 0,
          reviews: data.reviewslist || 0,
          blogs: data.blogslist || 0,
          events: data.eventslist || 0,
          news: data.newslist || 0,
          jobs: data.jobslist || 0,
          visitors: data.visitors || 0,
          consumerAccountsWithPlans: data?.consumerAccountsWithPlans || 0,
          revenue: data?.revenue || 0,
          traffic: data?.traffic || {
            visitors: 0,
            totalVisits: 0,
            sessions: 0,
            activeSessions: 0,
            pageViews: 0,
            avgSessionDuration: 0,
          },
        });
        setGrowthSeries({
          claimed: data?.growthSeries?.claimed || [],
          listings: data?.growthSeries?.listings || [],
          premium: data?.growthSeries?.premium || [],
        });
      } catch (error) {
        console.error("Dashboard API error", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardStats();
  }, [agency?._id, selectedRange]);

  const derivedStats = useMemo(() => {
    const all = Number(stats.allListings) || 0;
    const claimed = Number(stats.claimedListings) || 0;
    const premium = Number(stats.premiumListings) || 0;
    const visitors = Number(stats.visitors) || 0;

    const activitiesTotal =
      (stats.reviews || 0) +
      (stats.blogs || 0) +
      (stats.events || 0) +
      (stats.news || 0) +
      (stats.jobs || 0);

    const rangeDaysMap = {
      "7D": 7,
      "30D": 30,
      "90D": 90,
      "12M": 365,
    };

    const days = rangeDaysMap[selectedRange] || 7;

    // Dynamic max values
    const listingsMax = (50 / 30) * days;
    const visitorsMax = (500 / 30) * days;
    const activityMax = (60 / 30) * days;

    // 1️⃣ Listings Volume Score (0–100)
    const listingsVolumeScore = listingsMax > 0 ? Math.min((all / listingsMax) * 100, 100) : 0;

    // 2️⃣ Claim Rate Score (0–100)
    const claimRateScore = all > 0 ? Math.min((claimed / all) * 100, 100) : 0;

    // 3️⃣ Premium Conversion Score (0–100)
    const premiumConversionScore = claimed > 0 ? Math.min((premium / claimed) * 100, 100) : 0;

    // 4️⃣ Visibility Score (0–100)
    const visibilityScore = visitorsMax > 0 ? Math.min((visitors / visitorsMax) * 100, 100) : 0;

    // 5️⃣ Activity Score (0–100)
    const activityScore =
      activityMax > 0 ? Math.min((activitiesTotal / activityMax) * 100, 100) : 0;

    // 🎯 Final Weighted Score
    const finalScore = Math.round(
      listingsVolumeScore * 0.15 +
        claimRateScore * 0.2 +
        premiumConversionScore * 0.3 +
        visibilityScore * 0.2 +
        activityScore * 0.15
    );

    return {
      score: finalScore,

      listingsVolumeScore: Math.round(listingsVolumeScore),
      claimRateScore: Math.round(claimRateScore),
      premiumConversionScore: Math.round(premiumConversionScore),
      visibilityScore: Math.round(visibilityScore),
      activityScore: Math.round(activityScore),
    };
  }, [stats, selectedRange]);

  const getRevenueTrend = (revenue, range) => {
    const targets = {
      "7D": 2000,
      "30D": 10000,
      "90D": 20000,
      "12M": 50000,
    };

    const target = targets[range] || 0;

    if (!target) return { value: 0, positive: true };

    const value = Math.min((revenue / target) * 100, 100).toFixed(1);

    return {
      value: Math.abs(value).toFixed(1),
      positive: value >= 0,
    };
  };
  const getVistorstrend = (visitors, range) => {
    const targets = {
      "7D": 10000,
      "30D": 30000,
      "90D": 50000,
      "12M": 200000,
    };

    const target = targets[range] || 0;

    if (!target) return { value: 0, positive: true };

    const value = Math.min((visitors / target) * 100, 100).toFixed(1);

    return {
      value: Math.abs(value).toFixed(1),
      positive: value >= 0,
    };
  };

  const totalVisitors = Number(stats?.visitors) || 0;
  const trafficVisitors = Number(stats?.traffic?.visitors) || 0;

  let trafficData = 0;

  if (selectedRange === "12M") {
    trafficData = totalVisitors + trafficVisitors;
  } else if (selectedRange === "90D") {
    trafficData = totalVisitors / 9 + trafficVisitors;
  } else {
    trafficData = trafficVisitors;
  }

  const trafficTrend = getVistorstrend(trafficData, selectedRange);
  const revenueTrend = getRevenueTrend(stats?.revenue || 0, selectedRange);

  const overviewGrowthData = useMemo(() => {
    const rangeMap = {
      "7D": 7,
      "30D": 30,
      "90D": 90,
      "12M": 365,
    };

    const count = rangeMap[selectedRange] || 7;

    const labels = Array.from({ length: count }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (count - i - 1));
      return date.toISOString().split("T")[0]; // YYYY-MM-DD
    });

    // Helper: map series to labels
    const mapSeries = (series = []) => {
      const map = {};

      series.forEach((item) => {
        map[item.date] = item.value;
      });

      return labels.map((label) => map[label] || 0);
    };

    return {
      labels,
      datasets: [
        {
          label: "Claimed",
          data: mapSeries(growthSeries.claimed),
          borderColor: "rgba(36,108,255,0.4)",
          backgroundColor: "rgba(36,108,255,0.1)",
          tension: 0.4,
          fill: true,
          pointRadius: 1,
          pointHoverRadius: 2,
          borderWidth: 2,
        },
        {
          label: "Listing",
          data: mapSeries(growthSeries.listings),
          borderColor: "rgba(41,182,216,0.4)",
          backgroundColor: "rgba(41,182,216,0.1)",
          tension: 0.4,
          fill: true,
          pointRadius: 1,
          pointHoverRadius: 2,
          borderWidth: 2,
        },
        {
          label: "Premium",
          data: mapSeries(growthSeries.premium),
          borderColor: "rgba(164,76,226,0.4)",
          backgroundColor: "rgba(164,76,226,0.1)",
          tension: 0.4,
          fill: true,
          pointRadius: 1,
          pointHoverRadius: 2,
          borderWidth: 2,
        },
      ],
    };
  }, [growthSeries, selectedRange]);

  const engagementTotal =
    (stats.reviews || 0) +
    (stats.blogs || 0) +
    (stats.events || 0) +
    (stats.news || 0) +
    (stats.jobs || 0);

  const formatNumber = (num) => {
    if (!num) return "0";

    const n = Number(num);

    if (n >= 1_000_000) {
      return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    }

    if (n >= 1_000) {
      return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
    }

    // 🔥 FIX: round small numbers
    return Math.round(n).toString();
  };

  const formatDuration = (seconds = 0) => {
    const sec = Math.floor(seconds % 60);
    const min = Math.floor((seconds / 60) % 60);
    const hrs = Math.floor(seconds / 3600);

    if (hrs > 0) {
      return `${hrs}h ${min}m`;
    }

    if (min > 0) {
      return `${min}m ${sec}s`;
    }

    return `${sec}s`;
  };

  const arpc =
    stats?.revenue && stats?.consumerAccountsWithPlans
      ? stats.revenue / stats.consumerAccountsWithPlans
      : 0;

  console.log("derivedStats", derivedStats);
  if (loading) return <Loader />;

  return (
    <>
    <PageLayout>
      <SoftBox className="sd-dashboard sd-page">
        <div className="sd-dashboard__header sd-page__header">
          <PageTitle helpUrl={knowledgeBase?.dashboard}>Dashboard</PageTitle>
        </div>

        <Card className="sd-dashboard__card">
          <div className="sd-overview">
            <div className="sd-overview__container">
              <OverviewHeader
                selectedRange={selectedRange}
                onRangeChange={setSelectedRange}
                knowledgeBase={knowledgeBase}
                agency={agency}
                Update={Update}
                currency={currency}
                onCurrencyChange={setCurrency}
              />

              <div className="sd-overview__stats-grid">
            <StatOverviewCard
              title="Traffic"
              value={formatNumber(trafficData)}
              unit="Visitors"
              details={[
                `Page Views: ${formatNumber(stats?.traffic?.pageViews)}`,
                `Avg Duration: ${formatDuration(stats?.traffic?.avgSessionDuration)}`,
              ]}
              trend={{
                text: `${trafficTrend.positive ? "▲" : "▼"} ${trafficTrend.value}%`,
                positive: trafficTrend.positive,
              }}
            />

            <StatOverviewCard
              title="Listings"
              value={stats.allListings.toLocaleString()}
              unit="Listings"
              details={[
                `Free: ${stats.freeListings.toLocaleString()}`,
                `Claimed: ${stats.claimedListings.toLocaleString()}`,
                `Premium: ${stats.premiumListings.toLocaleString()}`,
              ]}
            />

            <StatOverviewCard
              title="Revenue"
              value={`${currency?.symbol}${stats?.revenue}`}
              unit={currency.code}
              details={[`Active Premium: ${stats?.consumerAccountsWithPlans}`, `ARPC: $${arpc}`]}
              trend={{
                text: `${revenueTrend.positive ? "▲" : "▼"} ${revenueTrend.value}%`,
                positive: revenueTrend.positive,
              }}
            />
            <StatOverviewCard
              title="Engagement"
              value={engagementTotal}
              unit={`Leads (${selectedRange})`}
              details={[
                `Reviews: ${stats.reviews.toLocaleString()}`,
                `Blogs: ${stats.blogs.toLocaleString()}`,
                `Events: ${stats.events.toLocaleString()}`,
              ]}
              leads={[
                `Newsfeed: ${stats.news.toLocaleString()}`,
                `Jobs: ${stats.jobs.toLocaleString()}`,
              ]}
            />
          </div>

          <GrowthCard chart={overviewGrowthData} />

          <div className="sd-overview__bottom-grid">
            <div className="sd-overview__left-col">
              <div className="sd-overview__left-upper">
                <MarketplaceHealthCard
                  free={stats.freeListings}
                  claimed={stats.claimedListings}
                  premium={stats.premiumListings}
                />
                <DirectoryHealthCard
                  score={derivedStats.score}
                  listingsVolume={derivedStats.listingsVolumeScore}
                  claimRate={derivedStats.claimRateScore}
                  premiumConversion={derivedStats.premiumConversionScore}
                  visibility={derivedStats.visibilityScore}
                  activity={derivedStats.activityScore}
                />
                {/* <MonetizationCard
                  mrr={derivedStats.mrr}
                  newPremium={stats.premiumListings}
                  churned={0}
                  conversionRate={derivedStats.conversionRate}
                /> */}
              </div>

              <FunnelSnapshotCard
                allListings={stats.allListings}
                claimed={stats.claimedListings}
                premium={stats.premiumListings}
              />
            </div>

            <div className="sd-overview__right-col">
              <NextBestActionsCard activities={activities} />
            </div>
          </div>
            </div>
          </div>
        </Card>
      </SoftBox>
    </PageLayout>
    </>
  );
}

export default Default;
